import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input, TextArea } from '../ui/Input';
import { OccasionPills, BudgetPills } from '../ui/PillSelector';
import { RecentPeopleRow } from '../person/PersonCard';
import { useConfetti } from '../ui/Confetti';
import { usePostHog } from 'posthog-js/react';

/**
 * GiftForm Component
 *
 * Quick capture form for adding a gift.
 * Designed for speed - get the essentials fast.
 */

export function GiftForm({
  onComplete,
  onCancel,
  people = [],
  preselectedPerson = null,
  initialData = {},
}) {
  const [formData, setFormData] = useState({
    recipientId: preselectedPerson?.id || '',
    recipientName: preselectedPerson?.recipientName || preselectedPerson?.name || '',
    description: '',
    occasion: 'birthday',
    date: new Date().toISOString().split('T')[0],
    budget: '',
    comments: '',
    ...initialData,
  });
  const [errors, setErrors] = useState({});
  const [showDetails, setShowDetails] = useState(false);
  const { trigger: triggerConfetti, Confetti } = useConfetti();
  const posthog = usePostHog();

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handlePersonSelect = (person) => {
    updateField('recipientId', person.id);
    updateField('recipientName', person.recipientName || person.name);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate
    const newErrors = {};
    if (!formData.recipientName.trim()) {
      newErrors.recipientName = 'Please select or enter a recipient';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Please describe the gift';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Success!
    posthog.capture('gift_added', {
      occasion: formData.occasion,
      has_budget: !!formData.budget,
      has_comments: !!formData.comments.trim(),
    });
    triggerConfetti(50, 30);
    setTimeout(() => {
      onComplete?.(formData);
    }, 300);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <Confetti />

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
        {/* Step 1: Who */}
        <div>
          <label className="block text-sm font-medium text-warm-700 mb-2">
            Who was this for?
          </label>

          {/* Recent people row */}
          {people.length > 0 && !formData.recipientId && (
            <RecentPeopleRow
              people={people.slice(0, 6)}
              onPersonClick={handlePersonSelect}
              className="mb-3"
            />
          )}

          {/* Selected person or input */}
          {formData.recipientId ? (
            <div className="flex items-center gap-3 p-3 bg-coral-50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-coral-200 flex items-center justify-center text-coral-700 font-medium">
                {formData.recipientName.charAt(0).toUpperCase()}
              </div>
              <span className="font-medium text-warm-900 flex-1">
                {formData.recipientName}
              </span>
              <button
                type="button"
                onClick={() => {
                  updateField('recipientId', '');
                  updateField('recipientName', '');
                }}
                className="text-warm-400 hover:text-warm-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <Input
              placeholder="Type a name or select above..."
              value={formData.recipientName}
              onChange={(e) => updateField('recipientName', e.target.value)}
              error={errors.recipientName}
            />
          )}
        </div>

        {/* Step 2: What */}
        <div>
          <label className="block text-sm font-medium text-warm-700 mb-2">
            What did you give?
          </label>
          <Input
            placeholder="Blue sweater from Nordstrom, $65"
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            error={errors.description}
            hint="Tip: Include store and price if you remember"
          />
        </div>

        {/* Step 3: When (Quick options) */}
        <div>
          <label className="block text-sm font-medium text-warm-700 mb-2">
            When was this?
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: 'lastWeek', label: 'Last week' },
            ].map((option) => {
              const isSelected = (() => {
                const today = new Date();
                const selected = new Date(formData.date);
                if (option.value === 'today') {
                  return selected.toDateString() === today.toDateString();
                }
                if (option.value === 'yesterday') {
                  const yesterday = new Date(today);
                  yesterday.setDate(yesterday.getDate() - 1);
                  return selected.toDateString() === yesterday.toDateString();
                }
                return false;
              })();

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    const date = new Date();
                    if (option.value === 'yesterday') {
                      date.setDate(date.getDate() - 1);
                    } else if (option.value === 'lastWeek') {
                      date.setDate(date.getDate() - 7);
                    }
                    updateField('date', date.toISOString().split('T')[0]);
                  }}
                  className={`pill ${isSelected ? 'pill-selected' : ''}`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => updateField('date', e.target.value)}
          />
        </div>

        {/* Optional details toggle */}
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center gap-2 text-sm text-coral-500 hover:text-coral-600"
        >
          <svg
            className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {showDetails ? 'Hide' : 'Add'} more details
        </button>

        {/* Optional details */}
        {showDetails && (
          <div className="space-y-6 animate-slide-up">
            {/* Occasion */}
            <div>
              <label className="block text-sm font-medium text-warm-700 mb-2">
                What was the occasion?
              </label>
              <OccasionPills
                selected={formData.occasion}
                onChange={(value) => updateField('occasion', value)}
              />
            </div>

            {/* Budget */}
            <div>
              <label className="block text-sm font-medium text-warm-700 mb-2">
                Price range
              </label>
              <BudgetPills
                selected={formData.budget}
                onChange={(value) => updateField('budget', value)}
              />
            </div>

            {/* Notes */}
            <div>
              <TextArea
                label="Notes (optional)"
                placeholder="Where you bought it, their reaction, etc..."
                value={formData.comments}
                onChange={(e) => updateField('comments', e.target.value)}
                rows={3}
              />
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 py-4 border-t border-warm-100 bg-white">
        <div className="flex gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
          >
            Save Gift
          </Button>
        </div>
      </div>
    </form>
  );
}

/**
 * QuickGiftCapture - Ultra-minimal gift entry
 */
export function QuickGiftCapture({
  onComplete,
  onCancel,
  recipientName = '',
}) {
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!description.trim()) {
      setError('Please describe the gift');
      return;
    }

    onComplete?.({
      description: description.trim(),
      date: new Date().toISOString().split('T')[0],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-5">
      <h2 className="font-display text-xl text-warm-900 mb-1">
        Quick Add Gift
      </h2>
      {recipientName && (
        <p className="text-sm text-warm-500 mb-4">
          For {recipientName}
        </p>
      )}

      <Input
        placeholder="What did you give?"
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
          setError('');
        }}
        error={error}
        autoFocus
      />

      <div className="flex gap-3 mt-4">
        <Button variant="ghost" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button variant="primary" type="submit" className="flex-1">
          Save
        </Button>
      </div>
    </form>
  );
}

export default GiftForm;
